/**
 * Every version of one patient's identity, newest first (DECISIONS.md #45).
 *
 * `PHASE-0.md` §2.5 asks that a correction creates a new version **with the old one visible** — of an
 * anteckning. #43 brought the same correction to the demographics and wrote only its half: version 2
 * is appended and version 1 stays in `clinical.patient` (AGENTS.md §3 rule 2). Storing the old
 * version is not the same as showing it, and nothing in the docs had ever asked for the showing here.
 * This is that screen, the demographic counterpart of `NoteVersionHistory` — until it existed the
 * only reader of a superseded patient row was `--verify-audit`.
 *
 * **Nothing is filtered, including a retraction.** The overview answers 404 for a patient retracted
 * as `entered-in-error`, because a retracted record must not read as current (`DATA-MODEL.md` §5).
 * Here the opposite is true: the question is what the record contains, and a version missing from a
 * history is the one thing this view exists to make impossible.
 *
 * **Asked for, not prefetched.** Reading it writes an audit row (AGENTS.md §4), so opening a patient
 * must not fetch a history nobody looked at and leave the log saying somebody read one. The row this
 * writes is truthful: a clinician did ask to see how the identity got to where it is.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  fetchPatientVersions,
  PatientHistoryError,
  type PatientHistory,
  type PatientHistoryFailure,
  type PatientVersion,
} from './patientVersionsApi'
import {
  patientVersionAuthor,
  patientVersionEffective,
  patientVersionException,
  patientVersionFields,
  patientVersionStanding,
  patientVersionWritten,
  type PatientHistoryTranslate,
} from './patientVersionsDisplay'

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly history: PatientHistory }
  | { readonly kind: 'failed'; readonly failure: PatientHistoryFailure }

function Version({
  version,
  previous,
  newest,
  t,
}: {
  readonly version: PatientVersion
  readonly previous: PatientVersion | null
  readonly newest: number
  readonly t: PatientHistoryTranslate
}) {
  const exception = patientVersionException(version, t)

  return (
    <li
      className={`patient-version${
        version.version === newest ? ' patient-version--current' : ''
      }`}
    >
      <header className="patient-version__header">
        <h4>{t('patient:history.number', { version: version.version })}</h4>

        <p className="patient-version__standing">
          {patientVersionStanding(version, newest, t)}
        </p>

        {/* A retraction, said before the values rather than after them. A reader who has already
            read a retracted name cannot unread it. */}
        {exception !== null && (
          <p className="patient-version__exception" role="status">
            {exception}
          </p>
        )}

        <p className="patient-version__author">{patientVersionAuthor(version, t)}</p>

        {/* Both times, each with its own label — never one of them bare beside the author's name,
            where it reads as when that author wrote (AGENTS.md §4, browser pass 2026-10-07).
            Always shown, unlike the overview, which shows `recordedAt` only when it differs from
            `effectiveAt`. In a history the time each version was written is the thing being looked
            at — and for a patient it is the only timestamp that moves, because a correction
            inherits `effective_at`. */}
        <p className="patient-version__effective">{patientVersionEffective(version, t)}</p>
        <p className="patient-version__written">{patientVersionWritten(version, t)}</p>
      </header>

      <dl className="patient-version__fields">
        {patientVersionFields(version, previous, t).map((field) => (
          <div
            key={field.key}
            className={`patient-version__field${
              field.changed ? ' patient-version__field--changed' : ''
            }`}
          >
            <dt>{field.label}</dt>
            <dd>
              {field.value}
              {/* In words, not a colour or an icon alone: the marker has to survive being read
                  aloud, printed, and looked at by somebody who does not know the convention. */}
              {field.changed && (
                <span className="patient-version__changed">
                  {' '}
                  {t('patient:history.changed')}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </li>
  )
}

/**
 * The identity history of one patient.
 *
 * Mounted only once a clinician has asked for it — see the module remarks — so it fetches on mount
 * and the parent controls whether it exists.
 */
export function PatientVersionHistory({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation(['common', 'patient', 'registration', 'correction'])

  const [state, setState] = useState<State>({ kind: 'loading' })

  // Bumped by the retry button, so a retry is a new attempt rather than an imperative refetch to
  // keep in step with the state. `NoteVersionHistory`'s pattern.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()

    setState({ kind: 'loading' })

    fetchPatientVersions(patientId, controller.signal).then(
      (history) => {
        if (!controller.signal.aborted) {
          setState({ kind: 'loaded', history })
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        setState({
          kind: 'failed',
          failure: cause instanceof PatientHistoryError ? cause.failure : 'unavailable',
        })
      },
    )

    return () => controller.abort()
  }, [patientId, attempt])

  // Sorted here rather than trusted from the server, for the reason `NoteVersionHistory` gives: an
  // order this screen assumed and the server changed would put a superseded version at the top of a
  // history, labelled as current.
  const versions =
    state.kind === 'loaded'
      ? [...state.history.versions].sort((left, right) => right.version - left.version)
      : []

  const newest = versions.reduce((highest, version) => Math.max(highest, version.version), 0)

  // By `versionId`, which is what `supersedes` actually names — see `patientVersionFields`.
  const byVersionId = new Map(versions.map((version) => [version.versionId, version]))

  return (
    <section className="patient-history" aria-label={t('patient:history.heading')}>
      <div aria-live="polite">
        {state.kind === 'loading' && (
          <p className="patient-history__notice">{t('patient:history.loading')}</p>
        )}

        {state.kind === 'failed' && (
          <div className="patient-history__notice patient-history__notice--error" role="alert">
            <p>
              {state.failure === 'not-found'
                ? t('patient:history.notFound')
                : t('patient:history.failed')}
            </p>

            {/* No retry for a patient who is not there — asking again teaches nothing. */}
            {state.failure === 'unavailable' && (
              <button type="button" onClick={() => setAttempt((value) => value + 1)}>
                {t('patient:history.retry')}
              </button>
            )}
          </div>
        )}

        {state.kind === 'loaded' && (
          <>
            <p className="patient-history__count">
              {t('patient:history.count', { count: versions.length })}
            </p>

            <ol className="patient-history__versions">
              {versions.map((version) => (
                <Version
                  key={version.versionId}
                  version={version}
                  previous={
                    version.supersedes === null
                      ? null
                      : byVersionId.get(version.supersedes) ?? null
                  }
                  newest={newest}
                  t={t}
                />
              ))}
            </ol>
          </>
        )}
      </div>
    </section>
  )
}
